import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, TokenType, HoldersResponse, HoldersSummary } from '@/lib/api'
import toast from 'react-hot-toast'

export function useHoldersSummary() {
  const { data, isLoading, error, refetch } = useQuery<HoldersSummary[]>({
    queryKey: ['holdersSummary'],
    queryFn: () => api.getHoldersSummary(),
    retry: 1,
    staleTime: 60000,
  })

  return {
    summary: data || [],
    isLoading,
    error,
    refetch,
  }
}

export function useTokenHolders(tokenType: TokenType, params?: { limit?: number; offset?: number }) {
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery<HoldersResponse>({
    queryKey: ['tokenHolders', tokenType, params],
    queryFn: () => api.getHolders(tokenType, params),
    retry: 1,
    staleTime: 30000,
  })

  const syncMutation = useMutation({
    mutationFn: () => api.syncHolders(tokenType),
    onSuccess: () => {
      toast.success(`Sincronización blockchain iniciada para ${tokenType}`)
      // Refetch after a short delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tokenHolders', tokenType] })
        queryClient.invalidateQueries({ queryKey: ['holdersSummary'] })
      }, 2000)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al sincronizar')
    },
  })

  const syncApiMutation = useMutation({
    mutationFn: () => api.syncHoldersFromApi(tokenType),
    onSuccess: () => {
      toast.success(`Sincronización API iniciada para ${tokenType}`)
      // Refetch after a short delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tokenHolders', tokenType] })
        queryClient.invalidateQueries({ queryKey: ['holdersSummary'] })
      }, 2000)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al sincronizar via API')
    },
  })

  return {
    data,
    holders: data?.holders || [],
    total: data?.total || 0,
    totalSupplyFormatted: data?.totalSupplyFormatted || '0',
    lastSyncAt: data?.lastSyncAt,
    isRunning: data?.isRunning || false,
    isLoading,
    error,
    refetch,
    sync: syncMutation.mutate,
    isSyncing: syncMutation.isPending,
    syncApi: syncApiMutation.mutate,
    isSyncingApi: syncApiMutation.isPending,
  }
}

export function useSyncAllHolders() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => api.syncAllHolders(),
    onSuccess: () => {
      toast.success('Sincronización iniciada para todos los tokens')
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tokenHolders'] })
        queryClient.invalidateQueries({ queryKey: ['holdersSummary'] })
      }, 2000)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al sincronizar')
    },
  })

  return {
    syncAll: mutation.mutate,
    isSyncing: mutation.isPending,
  }
}

export function useSyncTokenHolders(tokenType: TokenType) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => api.syncHoldersFromApi(tokenType),
    onSuccess: () => {
      toast.success(`Sincronización iniciada para ${tokenType}`)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tokenHolders', tokenType] })
        queryClient.invalidateQueries({ queryKey: ['holdersSummary'] })
      }, 2000)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al sincronizar')
    },
  })

  return {
    sync: mutation.mutate,
    isSyncing: mutation.isPending,
  }
}
